import React, { useMemo } from 'react';
import { useParams, useLocation } from 'react-router';

import { FiveHundredErrorPage } from '@sm/webassets';

import useTrackingDetails from '~pricing/hooks/useTrackingDetails';
import { usePackageData } from '~shared/hooks/usePackageData';
import { PackageType } from '~shared/types/pricing/package';
import { logPricingPageLoad } from '~pricing/pages/shared/index';
import { getPackagesBasedOnQueryStringIds, getPackageSetBasedOnPath } from '~pricing/lib/lib-helpers';
import { PackagesProvider } from '~shared/hooks/usePackages';
import { MatchParams } from './types';

export function withPageHandler<P>(WrappedComponent: React.ComponentType<P>): React.ComponentType<P> {
  return props => {
    const { canonicalPath = PackageType.Teams } = useParams<MatchParams>();
    const { search } = useLocation();
    const query = new URLSearchParams(search);
    const comparePlans = query.get('compare_plans');

    const { pageName, utSource } = useTrackingDetails();
    useMemo(() => {
      logPricingPageLoad({ pageName, utSource });
    }, [pageName, utSource]);

    const packages = usePackageData();

    // no data is no page
    const error = !Array.isArray(packages) || !packages.length;

    return (
      <Choose>
        {/* if there was an issue loading the data from pricingsvc, we've got nothing to show. bail */}
        <When condition={error}>
          <FiveHundredErrorPage />
        </When>
        <When condition={!error && packages.length}>
          <PackagesProvider
            packagesToDisplay={
              comparePlans
                ? getPackagesBasedOnQueryStringIds(comparePlans, canonicalPath)
                : getPackageSetBasedOnPath(canonicalPath)
            }
            showEduDiscount={canonicalPath === PackageType.Educational}
            comparePlans={Boolean(comparePlans)}
          >
            <WrappedComponent {...props} />
          </PackagesProvider>
        </When>
      </Choose>
    );
  };
}

export default withPageHandler;
